import { useState } from "react";

const MAX_TAGS = 5;
const MAX_TAG_LENGTH = 24;

function normalizeTag(raw) {
  let tag = raw.trim().toLowerCase();
  if (tag.startsWith("#")) tag = tag.slice(1);
  return tag;
}

function isValidTag(tag) {
  return /^[a-z0-9_]+$/.test(tag) && tag.length <= MAX_TAG_LENGTH;
}

export default function TagInput({ tags, setTags, maxTags = MAX_TAGS }) {
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");

  const addTag = (rawValue = inputValue) => {
    const normalized = normalizeTag(rawValue);

    if (!normalized) {
      setInputValue("");
      setError("");
      return;
    }

    if (!isValidTag(normalized)) {
      setError(
        `Tags must use letters, numbers, or underscores only, and be ${MAX_TAG_LENGTH} characters or fewer.`
      );
      return;
    }

    if (tags.includes(normalized)) {
      setInputValue("");
      setError("");
      return;
    }

    if (tags.length >= maxTags) {
      setError(`You can add up to ${maxTags} tags.`);
      return;
    }

    setTags((prev) => [...prev, normalized]);
    setInputValue("");
    setError("");
  };

  const removeTag = (tagToRemove) => {
    setTags((prev) => prev.filter((tag) => tag !== tagToRemove));
    setError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " " || e.code === "Space") {
      e.preventDefault();
      addTag();
    }

    if (e.key === "Backspace" && !inputValue && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addTag();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-gray-700">Tags</label>

      <div className="flex flex-wrap gap-2 rounded-xl border border-gray-300 p-3 focus-within:border-black">
        {tags.map((tag) => (
          <div key={tag} className="flex items-center gap-1 rounded-full border px-3 py-1 text-sm">
            <span>#{tag}</span>
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="text-gray-500 hover:text-black"
              aria-label={`Remove tag ${tag}`}
            >
              ×
            </button>
          </div>
        ))}

        {tags.length < maxTags && (
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder="Add a tag"
            className="min-w-[120px] flex-1 bg-transparent outline-none text-sm"
          />
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>Add up to {maxTags} tags</span>
        <span>
          {tags.length}/{maxTags}
        </span>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}
